import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { subscribe } from "../api/billing";
import { getMe } from "../api/me";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { SetupChecklist } from "../components/SetupChecklist";
import { SubscriptionBadge } from "../components/SubscriptionBadge";
import { t } from "../lib/i18n";

export function Home() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const freeStartInFlightRef = useRef(false);
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const freeStartMutation = useMutation({
    mutationFn: async () => {
      const expectedShopId = meQuery.data?.shop_id;
      if (!expectedShopId) {
        throw new Error(
          t("ストア情報を確認できませんでした。ページを再読み込みして再試行してください。"),
        );
      }

      // Free は Shopify の課金承認画面を作らない。サーバーが ACTIVE を返しても、
      // 同じストアの /api/me が Free へ収束したことを確認するまでは移動画面を開かない。
      const result = await subscribe("free");
      if (
        result.shop_id !== expectedShopId ||
        result.plan_code !== "free" ||
        result.confirmation_url !== null ||
        result.subscription_status.toLowerCase() !== "active"
      ) {
        throw new Error(
          t("Free プランの有効化を確認できませんでした。再試行してください。"),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ["me"],
        exact: true,
        refetchType: "none",
      });
      const authoritativeMe = await queryClient.fetchQuery({
        queryKey: ["me"],
        queryFn: getMe,
        staleTime: 0,
      });
      if (
        authoritativeMe.shop_id !== expectedShopId ||
        authoritativeMe.subscription.active !== true ||
        authoritativeMe.subscription.plan_code !== "free" ||
        authoritativeMe.subscription.status !== "active"
      ) {
        throw new Error(
          t("Free プランの有効化を確認できませんでした。再試行してください。"),
        );
      }
    },
    onSuccess: () => navigate("/stocky-csv"),
  });

  function startFreeDataImport() {
    if (
      meQuery.data?.needs_referral_probe !== false ||
      freeStartInFlightRef.current ||
      freeStartMutation.isPending
    ) {
      return;
    }
    freeStartInFlightRef.current = true;
    freeStartMutation.mutate(undefined, {
      onSettled: () => {
        freeStartInFlightRef.current = false;
      },
    });
  }

  return (
    <Page title="UreyukiBox">
      <Layout>
        {meQuery.isLoading && (
          <Layout.Section>
            <Card>
              <SkeletonBodyText />
            </Card>
          </Layout.Section>
        )}

        {meQuery.error && (
          <Layout.Section>
            <QueryErrorBanner onRetry={() => void meQuery.refetch()} />
          </Layout.Section>
        )}

        {meQuery.data && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      {t("ようこそ、")}
                      {meQuery.data.shopify_domain}
                    </Text>
                    <SubscriptionBadge subscription={meQuery.data.subscription} />
                  </InlineStack>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t("Stocky終了後の在庫管理をUreyukiBoxで継続できます。まずは在庫データを同期し、仕入先と発注点を設定してください。Stockyを使っていた場合は、公式エクスポートや保存済みCSV・画面貼り付けで対応データを取り込めます。")}
                  </Text>
                </BlockStack>
              </Card>
            </Layout.Section>

            {!meQuery.data.subscription.active && (
              <Layout.Section>
                <Banner
                  tone="info"
                  title={t("Stockyデータ取込はFreeプランで利用できます")}
                >
                  <p>
                    {t(
                      "Shopifyへの請求は発生しません。ボタンを押すとFreeプランを有効にし、データ取込へ進みます。",
                    )}
                  </p>
                  <Box paddingBlockStart="300">
                    <InlineStack gap="200" blockAlign="center">
                      <Button
                        variant="primary"
                        loading={freeStartMutation.isPending}
                        disabled={meQuery.data.needs_referral_probe !== false}
                        onClick={startFreeDataImport}
                      >
                        {t("Freeでデータ取込を始める")}
                      </Button>
                      <Button variant="plain" onClick={() => navigate("/plans")}>
                        {t("有料プランを比較する")}
                      </Button>
                    </InlineStack>
                  </Box>
                </Banner>
              </Layout.Section>
            )}

            {freeStartMutation.isError && (
              <Layout.Section>
                <Banner tone="critical" title={t("Free プランを開始できませんでした")}>
                  {freeStartMutation.error.message}
                </Banner>
              </Layout.Section>
            )}

            <Layout.Section>
              <SetupChecklist me={meQuery.data} />
            </Layout.Section>

            <Layout.Section>
              <Card>
                <InlineStack align="space-between" blockAlign="center" gap="200">
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingSm">
                      {t("困ったときは")}
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {t("エラーで画面が表示されないときの対処や、使い方をまとめています。")}
                    </Text>
                  </BlockStack>
                  <Button onClick={() => navigate("/help")}>{t("よくある質問")}</Button>
                </InlineStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
